import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import type { User } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { INVITE_ONLY_MESSAGE, isInviteGateFailure } from '@/lib/signupGate'

/** A sign-up that failed. `inviteOnly` marks the invite gate, which is not a
 *  fault the user can fix by retrying — the form offers the waitlist instead. */
export type SignUpFailure = { message: string; inviteOnly?: boolean }

/** The same user, unchanged, keeps its object. Prod, 2026-10-13: each
 *  instance set a fresh copy twice at startup (the auth event, then
 *  getSession), so every loader keyed on `user` fetched twice per instance. */
function sameUser(prev: User | null, next: User | null): User | null {
  if (!prev || !next || prev.id !== next.id) return next
  return JSON.stringify(prev) === JSON.stringify(next) ? prev : next
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)
  const [sessionLost, setSessionLost] = useState(false)
  const loadingRef = useRef(true)
  // True only while a signOut() call the user asked for is in flight — a
  // SIGNED_OUT that fires while this is true is expected, not a loss.
  const signingOutRef = useRef(false)
  // What the PREVIOUS auth event carried, so a SIGNED_OUT can tell "we had a
  // session and it just vanished" from "we were already signed out".
  const prevUserRef = useRef<User | null>(null)
  const prevExpiresRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    // Timeout to prevent infinite loading if Supabase is unreachable
    const timeout = setTimeout(() => {
      if (loadingRef.current) {
        console.warn('Auth check timed out after 5 seconds')
        loadingRef.current = false
        setLoading(false)
      }
    }, 5000)

    // Listen for auth changes — must be set up BEFORE exchangeCodeForSession
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // A session that just disappeared without the user asking (signOut())
        // is a LOST session, not a clean sign-out — expiry, revocation
        // elsewhere, or a bug. Silent before this: the user just saw the
        // login screen with no record anywhere (demo run 2026-09-06).
        if (event === 'SIGNED_OUT' && prevUserRef.current && !signingOutRef.current) {
          // Guarded: `localStorage` THROWS in a private window and wherever
          // site data is blocked, and an unguarded write here would take the
          // `setUser(null)` below down with it — leaving the app rendering a
          // signed-in shell over a session that is gone. The breadcrumb is a
          // nice-to-have; the state change is not.
          try {
            localStorage.setItem('symphony.auth.lostAt', new Date().toISOString())
          } catch { /* private mode / blocked site data */ }
          Sentry.captureEvent({
            message: 'auth.session_lost',
            level: 'warning',
            extra: {
              path: window.location.pathname,
              prevExpiresAt: prevExpiresRef.current,
            },
          })
          setSessionLost(true)
        }
        if (event === 'SIGNED_IN') {
          setSessionLost(false)
          signingOutRef.current = false
        }
        setUser((prev) => sameUser(prev, session?.user ?? null))
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true)
        }
        prevUserRef.current = session?.user ?? null
        prevExpiresRef.current = session?.expires_at
      }
    )

    // Handle PKCE code exchange (password recovery, email confirm, etc.)
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (code) {
      supabase.auth.exchangeCodeForSession(code)
        .then(() => {
          // Clean up the URL
          window.history.replaceState({}, '', window.location.pathname)
          loadingRef.current = false
          setLoading(false)
          clearTimeout(timeout)
        })
        .catch((error) => {
          console.error('Code exchange failed:', error)
          loadingRef.current = false
          setLoading(false)
          clearTimeout(timeout)
        })
    } else {
      // No code param — just get existing session
      supabase.auth.getSession()
        .then(({ data: { session } }) => {
          setUser((prev) => sameUser(prev, session?.user ?? null))
          prevUserRef.current = session?.user ?? null
          prevExpiresRef.current = session?.expires_at
          loadingRef.current = false
          setLoading(false)
          clearTimeout(timeout)
        })
        .catch((error) => {
          console.error('Auth session check failed:', error)
          loadingRef.current = false
          setLoading(false)
          clearTimeout(timeout)
        })
    }

    return () => {
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  // Set Sentry user context when user changes
  useEffect(() => {
    if (import.meta.env.PROD && user) {
      Sentry.setUser({
        id: user.id,
        email: user.email || undefined,
      })
    } else if (import.meta.env.PROD && !user) {
      Sentry.setUser(null)
    }
  }, [user])

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signUpWithEmail = async (
    email: string,
    password: string
  ): Promise<{ error: SignUpFailure | null }> => {
    // Ask the gate before trying. The trigger on auth.users enforces the same
    // rule, but it can only refuse by throwing, and GoTrue turns that into
    // "Database error saving new user" — which reads as a broken product to
    // somebody who just hasn't been invited yet. If the question itself fails
    // (offline, PostgREST down), fall through and let the server decide: the
    // gate is advisory, the trigger is the wall.
    const { data: allowed, error: gateError } = await supabase.rpc('signup_allowed', {
      p_email: email,
    })
    if (!gateError && allowed === false) {
      return { error: { message: INVITE_ONLY_MESSAGE, inviteOnly: true } }
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
    })
    // Belt and braces: the gate may have raced (an invite revoked between the
    // question and the answer), or the question may have failed above.
    if (isInviteGateFailure(error)) {
      return { error: { message: INVITE_ONLY_MESSAGE, inviteOnly: true } }
    }
    return { error }
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}`,
    })
    return { error }
  }

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (!error) {
      setIsPasswordRecovery(false)
    }
    return { error }
  }

  const signOut = async () => {
    signingOutRef.current = true
    const { error } = await supabase.auth.signOut()
    if (error) {
      // If the server session is already dead (revoked by a password change,
      // expired while idle), auth-js errors out WITHOUT clearing the local
      // session — session_not_found maps to AuthSessionMissingError, which
      // slips past its own 401/403/404 ignore list. Left alone, Sign out
      // silently does nothing forever. The user asked to leave: drop the
      // local session ourselves and reload to the login screen.
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-')) localStorage.removeItem(key)
      }
      window.location.reload()
    }
    return { error: null }
  }

  return {
    user,
    loading,
    isPasswordRecovery,
    /** True after a SIGNED_OUT the user did not ask for (Sign out excluded).
     *  Resets on the next SIGNED_IN. */
    sessionLost,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    resetPassword,
    updatePassword,
  }
}
